import {createApi, fetchBaseQuery} from '@reduxjs/toolkit/query/react'
import SearchResultsRequestType from "@SearchResults/api/types/SearchResultsRequest.type";
import ApiIntegrationResponseType from "@SearchResults/api/types/ApiIntegrationResponse.type";
import ApiIntegrationRequestType from "@SearchResults/api/types/ApiIntegrationRequest.type";
import updateSearchResultsWithApiRoutes from "@SearchResults/api/functions/updateSearchResultsWithApiRoutes";
import {getTotalApiProvidersCount} from "@SearchResults/api/functions/getTotalApiProvidersCount";
import updateResolvedApiRouteCount from "@SearchResults/api/functions/updateResolvedApiRouteCount";
import RouteSuggestionResponseType from "@SearchResults/api/types/RouteSuggestionResponse.type";
import RouteSuggestionRequestType from "@SearchResults/api/types/RouteSuggestionRequest.type";
import updateSearchResultsWithRouteSuggestions
    from "@SearchResults/api/functions/updateSearchResultsWithRouteSuggestions";
import updateResolvedSuggestedRouteCount from "@SearchResults/api/functions/updateResolvedSuggestedRouteCount";
import {SearchResultsRtkQueryResponseType} from "@SearchResults/api/types/SearchResultsRtkQueryResponse.type";

const getApiUrl = () => {
    const args = window.location.pathname.split("/")
    return `/${args[1]}/api/${args.splice(2, args.length).join("/")}`
}

// Define a service using a base URL and expected endpoints
export const searchResultsApi = createApi({
    refetchOnReconnect: true,
    refetchOnMountOrArgChange: 60 * 5,
    keepUnusedDataFor: 60 * 3,
    reducerPath: 'searchResultsApi',
    tagTypes: ['SearchResults'],
    baseQuery: fetchBaseQuery({
        headers: {
            Accept: 'application/json',
            "X-Requested-With": "XMLHttpRequest",
        },
    }),

    endpoints: (builder) => ({
        getSearchResults: builder.query<SearchResultsRtkQueryResponseType, SearchResultsRequestType>({
            query: () => ({
                url: getApiUrl(),
                method: 'GET',
            }),
            providesTags: ['SearchResults'],
            transformResponse: (response: SearchResultsRtkQueryResponseType) => {
                response.result.totalApiCount = getTotalApiProvidersCount(response.result.availableApiProviders)
                response.result.resolvedApiCount = 0
                response.result.resolvedReturnApiCount = 0
                response.result.failedApiCount = 0
                response.result.failedReturnApiCount = 0
                response.result.totalSuggestionsCount = response.suggestionData.routeSuggestionHubs.length
                response.result.resolvedSuggestionsCount = 0
                response.result.availableSingleApiProviders = response.result.availableApiProviders
                response.result.availableReturnApiProviders = response.result.availableApiProviders
                response.result.suggestedRoutes = []

                return response
            },
        }),
        getApiSearchResults: builder.mutation<ApiIntegrationResponseType, ApiIntegrationRequestType>({
            query: ({searchParams, ...params}) => ({
                url: '/get-api-routes-for-api-station-combinations',
                method: 'GET',
                params,
            }),
            async onQueryStarted({searchParams, ...patch}, {dispatch, queryFulfilled}) {
                try {
                    const {data} = await queryFulfilled
                    updateSearchResultsWithApiRoutes(
                        dispatch,
                        searchResultsApi,
                        searchParams,
                        data,
                        patch.isReturn
                    )
                } catch (error) {
                    updateResolvedApiRouteCount(dispatch, searchResultsApi, searchParams, patch.isReturn)
                }
            },
            transformResponse: (response: any) => {
                return response
            },
        }),
        getRouteSuggestions: builder.mutation<RouteSuggestionResponseType, RouteSuggestionRequestType>({
            query: ({searchParams, ...params}) => ({
                url: '/get-route-suggestions-for-hub-id',
                method: 'GET',
                params,
            }),
            async onQueryStarted({searchParams, ...patch}, {dispatch, queryFulfilled}) {
                try {
                    const {data} = await queryFulfilled
                    updateSearchResultsWithRouteSuggestions(
                        dispatch,
                        searchResultsApi,
                        searchParams,
                        data
                    )
                } catch (error) {
                    updateResolvedSuggestedRouteCount(dispatch, searchResultsApi, searchParams)
                }
            },
            transformResponse: (response: any) => {
                return response
            },
        }),
    }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
    useGetSearchResultsQuery,
    useGetApiSearchResultsMutation,
    useGetRouteSuggestionsMutation,
} = searchResultsApi